import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { getHrLeaveTransactions } from "../../api/api";
import EditTransactionDialog from "./components/EditTransactionDialog";

export default function LeaveTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [editTxn, setEditTxn] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const debounceTimerRef = useRef(null);
  const isFirstRender = useRef(true);

  // =====================================================
  // LOAD TRANSACTIONS
  // =====================================================

  const load = useCallback(
    async (pageNum = 1, searchTerm = "") => {
      setLoading(true);

      try {
        const cleanSearch = searchTerm.trim();

        const params = {
          page: pageNum,
          limit: pageSize,
        };

        // IMPORTANT:
        // Do not send search parameter when empty.
        // This makes clearing search behave exactly like
        // the normal transaction page.
        if (cleanSearch) {
          params.search = cleanSearch;
        }

        const data = await getHrLeaveTransactions(params);

        setTransactions(
          Array.isArray(data?.transactions)
            ? data.transactions
            : []
        );

        setTotalCount(
          Number(data?.pagination?.total_count || 0)
        );

        setTotalPages(
          Number(data?.pagination?.total_pages || 0)
        );
      } catch (err) {
        console.error(
          "Failed to load transactions:",
          err
        );

        setTransactions([]);
        setTotalCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    load(1, "");
  }, [load]);

  // =====================================================
  // DEBOUNCE SEARCH
  // =====================================================

  useEffect(() => {
    // Skip debounce logic on initial render.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedSearch = search.trim();

    // ===================================================
    // SEARCH CLEARED
    // ===================================================
    //
    // If user removes all search text:
    //
    // 1. Reset to page 1
    // 2. Clear debounced search
    // 3. Load normal unfiltered transactions
    //
    // This avoids keeping the previous search results.
    // ===================================================

    if (trimmedSearch === "") {
      setDebouncedSearch("");

      if (page !== 1) {
        setPage(1);
      } else {
        load(1, "");
      }

      return;
    }

    // ===================================================
    // NORMAL SEARCH
    // ===================================================

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(trimmedSearch);

      // Always start search results from page 1.
      if (page !== 1) {
        setPage(1);
      }
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  // =====================================================
  // LOAD WHEN PAGE / DEBOUNCED SEARCH CHANGES
  // =====================================================

  useEffect(() => {
    // Initial load is already handled above.
    if (isFirstRender.current) {
      return;
    }

    load(page, debouncedSearch);
  }, [page, debouncedSearch, load]);

  // =====================================================
  // SEARCH CHANGE
  // =====================================================

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  // =====================================================
  // PAGE CHANGE
  // =====================================================

  const handlePageChange = (event, newPage) => {
    setPage(newPage);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // EDIT SUCCESS
  // =====================================================

  const handleEditSuccess = async () => {
    setEditTxn(null);

    await load(page, debouncedSearch);
  };

  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDate = (date) => {
    if (!date) return "—";

    return new Date(date).toLocaleDateString();
  };

  // =====================================================
  // TRANSACTION TYPE COLOR
  // =====================================================

  const getTransactionTypeColor = (type) => {
    if (type?.includes("credit")) {
      return "success";
    }

    if (type?.includes("debit")) {
      return "error";
    }

    return "default";
  };

  // =====================================================
  // INITIAL LOADING
  // =====================================================

  if (loading && transactions.length === 0 && page === 1) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>

      {/* =================================================
          HEADER
      ================================================= */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
          }}
        >
          Leave Transactions
        </Typography>

        <TextField
          size="small"
          placeholder="Search by name, email, or emp code"
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{
                    color: "text.secondary",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 340,
            maxWidth: "100%",
            background: "#fff",
          }}
        />
      </Box>

      {/* =================================================
          TOTAL COUNT
      ================================================= */}

      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          Total: {totalCount} transactions

          {search.trim() !== debouncedSearch && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              searching...
            </Typography>
          )}
        </Typography>
      </Box>

      {/* =================================================
          TABLE
      ================================================= */}

      <Box sx={{ position: "relative" }}>

        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              pt: 6,
              backgroundColor: "rgba(255,255,255,0.55)",
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        <TableContainer
          component={Paper}
          variant="outlined"
        >
          <Table size="small">

            {/* =================================================
                TABLE HEADER
            ================================================= */}

            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    background: "#f5f7fa",
                  },
                }}
              >
                <TableCell>Date</TableCell>

                <TableCell>User</TableCell>

                <TableCell>Type</TableCell>

                <TableCell align="right">
                  Days
                </TableCell>

                <TableCell align="right">
                  Before
                </TableCell>

                <TableCell align="right">
                  After
                </TableCell>

                <TableCell>
                  Remark
                </TableCell>

                <TableCell
                  align="center"
                  width={50}
                >
                  Edit
                </TableCell>
              </TableRow>
            </TableHead>

            {/* =================================================
                TABLE BODY
            ================================================= */}

            <TableBody>
              {transactions.map((txn) => (
                <TableRow
                  key={txn.id}
                  hover
                >

                  {/* DATE */}

                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(txn.created_at)}
                    </Typography>
                  </TableCell>

                  {/* USER */}

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      {txn.user_name}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {txn.emp_code}
                    </Typography>
                  </TableCell>

                  {/* TYPE */}

                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        txn.transaction_type
                          ?.replace(/_/g, " ")
                          .toUpperCase()
                      }
                      color={getTransactionTypeColor(
                        txn.transaction_type
                      )}
                      variant="outlined"
                    />
                  </TableCell>

                  {/* DAYS */}

                  <TableCell align="right">
                    <Typography variant="body2">
                      {txn.days}
                    </Typography>
                  </TableCell>

                  {/* BEFORE */}

                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {txn.balance_before}
                    </Typography>
                  </TableCell>

                  {/* AFTER */}

                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {txn.balance_after}
                    </Typography>
                  </TableCell>

                  {/* REMARK */}

                  <TableCell>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {txn.remarks}
                    </Typography>
                  </TableCell>

                  {/* EDIT */}

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => setEditTxn(txn)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>

                </TableRow>
              ))}

              {/* =================================================
                  EMPTY STATE
              ================================================= */}

              {transactions.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{
                      py: 4,
                      color: "text.secondary",
                    }}
                  >
                    {search.trim()
                      ? "No transactions found for this search."
                      : "No transactions found."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

          </Table>
        </TableContainer>
      </Box>

      {/* =================================================
          PAGINATION
      ================================================= */}

      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            disabled={loading}
          />
        </Box>
      )}

      {/* =================================================
          EDIT DIALOG
      ================================================= */}

      <EditTransactionDialog
        open={!!editTxn}
        onClose={() => setEditTxn(null)}
        onSuccess={handleEditSuccess}
        transaction={editTxn}
      />

    </Box>
  );
}
